// =====================================================================================
// INVRT Radar - web console - the operator console (story P2-UI: three screens, one job)
//
// Mark's ruling: the operator console is ONE SCREEN PER JOB on the DS Nav Rail, not one
// endless page. The former single OperatorPage split into three routes:
//   /operator/catalogue      the E03 product hierarchy
//   /operator/access         the E04A assignments for one chosen organisation
//   /operator/organisations  the estate's organisations (create + invite)
// and Billing is REMOVED from the console entirely (the money ruling: no HIGH-INTENT
// money machinery while clients are INVRT-billed; lib/billingAdmin and the PS7 section
// left the codebase with it).
//
// The access screen keeps the selector's law: default to All organisations and ask the
// operator to choose rather than administering an arbitrary org; when exactly ONE visible
// organisation is active, All and that organisation are the same set, so it is selected
// outright (a choice that could not change anything is not offered as a decision). Probe
// and test organisations never appear by default: they sit behind the Show test records
// toggle (testRecords.ts), and the auto-select never auto-selects one.
//
// The catalogue is platform-wide by contract: the E03 catalogue routes take no
// organisation parameter, so the selector governs the access section only. Authority
// stays on the server: these screens render states, they never decide them.
// =====================================================================================

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Boxes, Building2, KeyRound } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState } from "@/components/States";
import { SearchField } from "@/components/ui/search-field";
import { Select } from "@/components/ui/input";
import { CatalogueSection } from "@/pages/operator/CatalogueSection";
import { AccessSection } from "@/pages/operator/AccessSection";
import { AssignCampaignPanel } from "@/pages/operator/AssignCampaignPanel";
import { RetireBindingPanel } from "@/pages/operator/RetireBindingPanel";
import { OrganisationsSection } from "@/pages/operator/OrganisationsSection";
import { hiddenTestRecordsLine, isTestOrganisation, TEST_RECORDS_TOGGLE_LABEL } from "@/pages/operator/testRecords";
import { useOperatorOrganisations } from "@/pages/operator/useOperatorOrganisations";

function ScreenFrame({
  icon,
  title,
  blurb,
  children,
}: {
  icon: ReactNode;
  title: string;
  blurb: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start gap-3">
        {icon}
        <div>
          <h1 className="text-xl font-semibold text-ink">{title}</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-muted">{blurb}</p>
        </div>
      </header>
      {children}
    </div>
  );
}

function OperatorDenial() {
  return (
    <EmptyState
      title="Catalogue and access is for INVRT platform operators"
      body="This area is for INVRT platform operators. The server also refuses requests without super-admin access."
    />
  );
}

/** Normalises a screen filter exactly once per render, as the console header always did. */
function useNormalisedFilter(filter: string): string {
  return useMemo(() => filter.trim().toLocaleLowerCase(), [filter]);
}

// ---------------------------------------------------------------------------------------
// /operator/catalogue
// ---------------------------------------------------------------------------------------

export function OperatorCatalogueScreen() {
  const { isSuperAdmin } = useSession();
  const [filter, setFilter] = useState("");
  const normalisedFilter = useNormalisedFilter(filter);

  if (!isSuperAdmin) return <OperatorDenial />;

  return (
    <ScreenFrame
      icon={<Boxes className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />}
      title="Catalogue"
      blurb="The RADAR product hierarchy: products, services, modules and campaigns. Every subscription and grant points at it. Server checks, not this screen, decide authority."
    >
      <SearchField
        label="Filter catalogue"
        value={filter}
        onChange={setFilter}
        placeholder="Search product, service, module or campaign"
        className="max-w-xl"
      />
      <CatalogueSection filter={normalisedFilter} />
    </ScreenFrame>
  );
}

// ---------------------------------------------------------------------------------------
// /operator/access
// ---------------------------------------------------------------------------------------

export function OperatorAccessScreen() {
  const { isSuperAdmin } = useSession();
  const [filter, setFilter] = useState("");
  const [orgId, setOrgId] = useState("");
  // Probe/test organisations stay behind the explicit toggle, default OFF (story P2-UI).
  const [showTestRecords, setShowTestRecords] = useState(false);
  const normalisedFilter = useNormalisedFilter(filter);

  const organisations = useOperatorOrganisations({ enabled: isSuperAdmin });
  const allActive = useMemo(
    () => (organisations.data ?? []).filter((organisation) => organisation.status === "active"),
    [organisations.data],
  );
  const testRecordCount = useMemo(
    () => allActive.filter((organisation) => isTestOrganisation(organisation.name)).length,
    [allActive],
  );
  const activeOrganisations = useMemo(
    () =>
      showTestRecords
        ? allActive
        : allActive.filter((organisation) => !isTestOrganisation(organisation.name)),
    [allActive, showTestRecords],
  );

  useEffect(() => {
    // Auto-select only a VISIBLE organisation: the toggle being off must never make the
    // console administer a probe org by accident.
    if (!orgId && activeOrganisations.length === 1) setOrgId(activeOrganisations[0]!.id);
  }, [orgId, activeOrganisations]);

  if (!isSuperAdmin) return <OperatorDenial />;

  return (
    <ScreenFrame
      icon={<KeyRound className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />}
      title="Access"
      blurb="Administer which organisation can use which module and campaign, and who in it. Server checks, not this screen, decide authority."
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <SearchField
          label="Filter access"
          value={filter}
          onChange={setFilter}
          placeholder="Search module, campaign or person"
          className="lg:max-w-xl"
        />
        {allActive.length > 0 ? (
          <Select
            label="Organisation"
            value={orgId}
            onChange={(event) => setOrgId(event.target.value)}
            className="h-11 lg:w-72"
            hint="The access lists below administer the chosen organisation."
          >
            <option value="">All organisations</option>
            {activeOrganisations.map((organisation) => (
              <option key={organisation.id} value={organisation.id}>
                {organisation.name} ({organisation.status})
              </option>
            ))}
          </Select>
        ) : null}
      </div>
      {allActive.length > 0 && testRecordCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
            <input
              type="checkbox"
              checked={showTestRecords}
              onChange={(event) => setShowTestRecords(event.target.checked)}
              className="h-5 w-5 rounded border-field-border text-primary accent-primary"
            />
            {TEST_RECORDS_TOGGLE_LABEL}
          </label>
          {!showTestRecords ? (
            <p className="text-sm text-ink-muted">{hiddenTestRecordsLine(testRecordCount)}</p>
          ) : null}
        </div>
      ) : null}
      {/* When NO organisation is active the AccessSection renders its own honest
          "No active organisations" empty state; nothing extra is added here. */}
      <AccessSection filter={normalisedFilter} orgId={orgId} />
      <AssignCampaignPanel />
      <RetireBindingPanel />
    </ScreenFrame>
  );
}

// ---------------------------------------------------------------------------------------
// /operator/organisations
// ---------------------------------------------------------------------------------------

export function OperatorOrganisationsScreen() {
  const { isSuperAdmin } = useSession();
  const [filter, setFilter] = useState("");
  const normalisedFilter = useNormalisedFilter(filter);

  if (!isSuperAdmin) return <OperatorDenial />;

  return (
    <ScreenFrame
      icon={<Building2 className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />}
      title="Organisations"
      blurb="Every organisation in the estate: create one, then invite its people. The server checks authority, parents, roles and catalogue codes."
    >
      <SearchField
        label="Filter organisations"
        value={filter}
        onChange={setFilter}
        placeholder="Search organisations"
        className="max-w-xl"
      />
      <OrganisationsSection filter={normalisedFilter} />
    </ScreenFrame>
  );
}
